import { useQuery } from '@tanstack/react-query';
import { useApi, type InvoiceResponse } from '@/shared/api';
import { canManage, canViewGuestPii, useAuth } from '@/shared/auth';
import { useObjectScope } from '@/shared/object-scope';
import { invoiceStepState } from './bookingInvoiceModel';
import { onboardingDone } from './helpers';

/**
 * Gastprofil der Buchung: keins, für dieses Konto verborgen (Rolle verwaltet, aber ohne
 * Gastdaten-Recht), lädt, Fehler oder da. Eine abgeschaltete Abfrage bliebe sonst ewig „lädt“.
 */
export type GuestState = 'none' | 'hidden' | 'loading' | 'error' | 'ready';

/**
 * Daten des Buchungs-Arbeitsraums: Buchung, Gast (nur mit Gastdaten-Recht), Apartment,
 * Rechnungen (nur Verwalter) und was daraus folgt. Abgeleitet wird aus vorhandenen Daten, nicht
 * aus `isSuccess` – ein misslungenes Nachladen löscht keinen bekannten Stand.
 */
export function useBookingWorkspace(id: string) {
  const api = useApi();
  const { user } = useAuth();
  const { groups } = useObjectScope();
  const manageable = canManage(user);
  const canSeeGuests = canViewGuestPii(user);
  const booking = useQuery({
    queryKey: ['bookings', 'detail', id],
    queryFn: () => api.bookings.get(id),
  });
  const data = booking.data;
  const guest = useQuery({
    queryKey: ['guests', 'detail', data?.guestProfileId ?? ''],
    queryFn: () => api.guests.get(data!.guestProfileId!),
    enabled: !!data?.guestProfileId && canSeeGuests,
  });
  const property = useQuery({
    queryKey: ['properties', 'detail', data?.propertyId ?? ''],
    queryFn: () => api.properties.get(data!.propertyId),
    enabled: !!data,
  });
  const invoices = useQuery<InvoiceResponse[]>({
    queryKey: ['invoice', 'booking', id],
    queryFn: () => api.invoices.listForBooking(id),
    enabled: !!data && manageable,
  });
  const object = groups.find((group) => group.apartments.some((a) => a.id === data?.propertyId));
  const listed = object?.apartments.find((a) => a.id === data?.propertyId);
  // Ohne Gastdaten-Recht löscht der Server `guestProfileId` (StripGuestPii) – „keins“ wäre geraten.
  const guestState: GuestState = !canSeeGuests
    ? 'hidden'
    : !data?.guestProfileId
      ? 'none'
      : guest.data
        ? 'ready'
        : guest.isError
          ? 'error'
          : 'loading';
  const guestName = guest.data
    ? `${guest.data.firstName} ${guest.data.lastName}`.trim()
    : (data?.guestName ?? null);
  const name = property.data ?? listed;
  return {
    api,
    booking,
    guest,
    guestState,
    invoices,
    manageable,
    canSeeGuests,
    guestName: guestName || null,
    propertyName: name
      ? (name.adminDisplayName ?? name.name)
      : property.isError
        ? 'Apartment nicht geladen'
        : null,
    apartments: object?.apartments ?? [],
    // Ohne Profil ist das Onboarding offen; verborgen, ladend oder fehlerhaft ist es unbekannt.
    onboardingComplete: !manageable
      ? null
      : guestState === 'none'
        ? false
        : guestState === 'ready'
          ? onboardingDone(guest.data)
          : null,
    invoiceState: manageable && invoices.data ? invoiceStepState(invoices.data) : null,
  };
}
